// ToolInvocationItem — tool call header; click (or Show all / live focus) reveals result.

import { useEffect, useState, type ReactNode } from 'react';
import type { ProgressStep } from '@localagent/sdk';
import { formatInlineArgs } from './activity-feed';
import { SubAgentActivityItem } from './SubAgentActivityItem';
import type { SubAgentFeed } from './activity-feed';
import { getToolResultRenderer } from './tool-result-renderers';

export interface ToolInvocationItemProps {
  step: ProgressStep;
  child?: SubAgentFeed;
  /** Nested feed body when `child` is set (rendered by parent to avoid circular imports). */
  childContent?: ReactNode;
  /**
   * Parent-driven expand (live focus on latest step, or Show all).
   * Clicking the header toggles a local override until the parent focus changes.
   */
  detailsOpen?: boolean;
}

function StatusLabel({ status, error }: { status: string | undefined; error: string | undefined }) {
  const isError = status === 'error' || (error != null && error !== '');
  if (!status && !error) {
    return <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400">Running…</span>;
  }
  return (
    <span
      data-testid="tool-invocation-status"
      className={`shrink-0 text-xs ${
        isError ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
      }`}
    >
      {isError ? status ?? 'error' : status ?? 'success'}
    </span>
  );
}

export function ToolInvocationItem({
  step,
  child,
  childContent,
  detailsOpen = false,
}: ToolInvocationItemProps) {
  const toolName = step.toolName ?? 'unknown tool';
  const inFlight = step.toolResultStatus == null && !step.toolResultError;
  const inlineArgs = formatInlineArgs(step.toolArgsPreview ?? '');
  const resultText = step.toolResultPreview ?? step.toolResultError ?? '';
  const isError =
    step.toolResultStatus === 'error' ||
    (step.toolResultError != null && step.toolResultError !== '');
  const ResultRenderer = getToolResultRenderer(toolName);

  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setOverride(null);
  }, [detailsOpen]);

  const open = override ?? detailsOpen;

  return (
    <div
      data-testid="tool-invocation"
      data-tool-name={toolName}
      data-details-open={open}
      className="w-full min-w-0 py-0"
    >
      <button
        type="button"
        data-testid="tool-invocation-toggle"
        aria-expanded={open}
        onClick={() => setOverride(!open)}
        className="flex w-full min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left text-sm hover:text-foreground"
      >
        <StatusLabel status={step.toolResultStatus} error={step.toolResultError} />
        <span className="shrink-0 font-medium text-foreground">{toolName}</span>
        {inlineArgs ? (
          <span
            data-testid="tool-invocation-args"
            className="min-w-0 truncate font-sans text-foreground/90"
            title={inlineArgs}
          >
            {inlineArgs}
          </span>
        ) : null}
      </button>

      {open && (
        <div className="mt-0.5 min-w-0 pl-1">
          {inFlight ? (
            <p className="text-xs text-muted-foreground">Calling {toolName}…</p>
          ) : (
            <div data-testid="tool-invocation-result" className="min-w-0 text-sm">
              {isError ? (
                <p className="whitespace-pre-wrap break-words text-red-600 dark:text-red-400">
                  {resultText || 'Error'}
                </p>
              ) : resultText ? (
                <ResultRenderer text={resultText} toolName={toolName} />
              ) : (
                <p className="text-xs italic text-muted-foreground">No content</p>
              )}
            </div>
          )}

          {child && (
            <SubAgentActivityItem feed={child}>{childContent}</SubAgentActivityItem>
          )}
        </div>
      )}
    </div>
  );
}
